import type { BaseBridge } from './bridges/BaseBridge';
import { WebBridge, type WebBridgeOptions } from './bridges/WebBridge';
import type { IOSBridgeOptions } from './bridges/IOSBridge';
import { IOSBridge } from './bridges/IOSBridge';
import type { AndroidBridgeOptions } from './bridges/AndroidBridge';
import { AndroidBridge } from './bridges/AndroidBridge';
import type { DocsUpdatedPayload, OneSearchByKeyType } from './types';

export interface HaloBridgeOptions
  extends Omit<WebBridgeOptions, 'contentWindow'>,
    IOSBridgeOptions,
    AndroidBridgeOptions {}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface HaloBridge extends BaseBridge {
  request(method: 'getTokens'): Promise<{ id_token: string }>;
  request(method: 'getContacts'): Promise<
    Array<{
      avatar: string;
      avatar_color: string;
      first_name: string;
      last_name: string;
      name: string;
      original_name: string;
      remark_first_name: string;
      remark_last_name: string;
      remark_name: string;
      secondary_name: string;
      state: number;
      user_id: string;
      username: string;
    }>
  >;
  request(method: 'getSessions'): Promise<
    Array<{
      avatar: string;
      chat_id: string;
      name: string;
    }>
  >;
  request(method: 'getGroups'): Promise<
    Array<{
      avatar: string;
      chat_id: string;
      name: string;
    }>
  >;
  request(
    method: 'shareLink',
    params: { url: string }
  ): Promise<{
    userIds: string[];
    chatIds: string[];
  }>;
  request(method: 'openDocLink', params: { url: string }): Promise<void>;
  request(
    method: 'oneSearchByKey',
    params: { keywords: string; types: OneSearchByKeyType[] }
  ): Promise<Record<string, Record<string, any>[]>>;
  request(
    method: 'getSessionSimpleInfos',
    params: { page: number; page_size: number }
  ): Promise<{
    list: Array<{ id: string; chat_id: string; name: string; username: string; avatar: string }>;
    total: number;
  }>;
  notify(method: 'docsUpdated', params: DocsUpdatedPayload): void;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class HaloBridge {
  constructor(options: HaloBridgeOptions) {
    let instance: BaseBridge | null = null;

    if (IOSBridge.iosBridge) {
      instance = new IOSBridge();
    } else if (AndroidBridge.androidBridge) {
      instance = new AndroidBridge();
    } else if (window.parent && window.parent !== window) {
      instance = new WebBridge({
        ...options,
        contentWindow: window.parent,
      });
    }

    return instance as unknown as HaloBridge;
  }
}
